/**
 * Weight units — the single conversion + plate-rounding boundary (WEB MIRROR).
 *
 * Storage is ALWAYS canonical pounds (lb); nothing in the DB or API changes.
 * Units are a per-viewer DISPLAY preference: convert canonical lb → the viewer's
 * unit on the way OUT, and an entered value → canonical lb (full precision) on
 * the way IN. Round only at DISPLAY (`toDisplayWeight` / `roundToPlate`), never
 * the stored canonical value (`toCanonicalLb` keeps full precision) — that's what
 * keeps a kg user's "60" round-tripping back to exactly "60".
 *
 * MIRROR of the mobile `nubod-mobile/lib/units.ts`. The two hand-mirrored copies
 * must NOT drift — they share the golden fixtures in `units.test.js`. If you
 * change a number here, change it in the mobile copy and update both tests.
 *
 * NOTE: workout weights only. Body-measurement weight is dual-stored as
 * `weight.{kg,lb}` and handled separately (StatsPage / MeasurementModal).
 */

/** @typedef {'kg' | 'lb'} WeightUnit */

/** Exact NIST conversion factor (matches mobile; NOT the rough 2.205). */
export const KG_PER_LB = 0.45359237;

export function kgToLb(kg) {
  return kg / KG_PER_LB;
}

export function lbToKg(lb) {
  return lb * KG_PER_LB;
}

/**
 * Plate-grid granularity per unit — the smallest LOADABLE jump. Used by
 * `roundToPlate` for things that must land on a real plate (a suggestion or a
 * +/- step). NOT used for displaying a stored value (`toDisplayWeight`).
 */
const PLATE_GRID = {
  lb: { micro: 0.5, microBelow: 20, standard: 2.5 },
  kg: { micro: 0.5, microBelow: 10, standard: 1.25 },
};

/** Snap a weight (already in `unit`) to the nearest LOADABLE plate. */
export function roundToPlate(weight, unit) {
  if (!Number.isFinite(weight) || weight <= 0) return 0;
  const g = PLATE_GRID[unit] || PLATE_GRID.lb;
  const inc = weight < g.microBelow ? g.micro : g.standard;
  return Math.round(weight / inc) * inc;
}

/**
 * Canonical lb → the viewer's unit for DISPLAY. Rounds to 2 dp only — enough to
 * kill float noise and recover the user's EXACT entered value (a 32 kg dumbbell
 * shows 32, not a plate-snapped 32.5). Cross-unit views show the faithful
 * conversion (60 kg → 132.28 lb), not a snapped number — by design.
 */
export function toDisplayWeight(canonicalLb, unit) {
  const n = Number(canonicalLb);
  if (!Number.isFinite(n)) return 0;
  const inUnit = unit === 'kg' ? lbToKg(n) : n;
  return Math.round(inUnit * 100) / 100;
}

/**
 * A user-entered value (in their unit) → canonical lb. FULL PRECISION — never
 * round here, or the kg → lb → kg round-trip drifts.
 */
export function toCanonicalLb(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return unit === 'kg' ? kgToLb(n) : n;
}

/** "60 kg" / "132.28 lb" — display string for a canonical-lb weight. */
export function formatWeight(canonicalLb, unit) {
  return `${toDisplayWeight(canonicalLb, unit)} ${unit}`;
}

/**
 * PO step in the VIEWER's unit. Mirrors the lb engine (+2.5 below 100 lb, +5
 * at/above) on a single canonical-lb threshold, so trainer (server/web) and
 * client (mobile) agree. kg steps (1.25 / 2.5) are the metric mirror.
 */
export function plateIncrement(canonicalLb, unit) {
  const big = Number(canonicalLb) >= 100; // 100 lb ≈ 45 kg
  if (unit === 'kg') return big ? 2.5 : 1.25;
  return big ? 5 : 2.5;
}

/** isMetric → the viewer's WeightUnit. The single profile→unit resolver. */
export function unitForMetric(isMetric) {
  return isMetric ? 'kg' : 'lb';
}
